<template>
  <div class="postcode-lookup-container">
    <b-form-group
      :label="postcodeLabel"
      label-for="postcodeLookup"
      :label-class="required ? 'required-field' : ''"
    >
      <template v-slot:label v-if="required && postcodeLabel">
        {{ postcodeLabel }} <span class="text-danger">*</span>
      </template>
      
      <div class="postcode-input-wrapper" ref="wrapper">
        <b-form-input
          id="postcodeLookup"
          ref="postcodeInput"
          v-model="localPostcode"
          type="text"
          class="form-control"
          :placeholder="postcodePlaceholder"
          :disabled="disabled"
          :state="isPostcodeValid"
          autocomplete="off"
          @input="onPostcodeInput"
          @keydown.down.prevent="onPostcodeArrowDown"
          @keydown.up.prevent="onPostcodeArrowUp"
          @keydown.enter.prevent="onPostcodeEnterKeyPress"
          @blur="onPostcodeBlur"
          @focus="onPostcodeFocus"
        />
        
        <div 
          v-if="showPostcodeDropdown && filteredPostcodes.length > 0" 
          class="postcode-dropdown"
        >
          <div 
            v-for="(postcode, index) in filteredPostcodes" 
            :key="index"
            class="dropdown-item"
            :class="{ 'active': activePostcodeIndex === index }"
            @click="selectPostcode(postcode)"
            @mouseenter="activePostcodeIndex = index"
          >
            {{ postcode }}
          </div>
        </div>
        
        <div v-if="isPostcodeLoading" class="loading-indicator">
          <b-spinner small type="grow" />
          <span>Loading...</span>
        </div>
      </div>
      
      <div v-if="showPostcodeError" class="error-message">
        Please select a valid postcode from the list.
      </div>
      
      <div v-if="postcodeValidationError" class="error-message">
        {{ postcodeValidationError }}
      </div>
    </b-form-group>

    <b-form-group
      :label="addressLabel"
      label-for="addressLookup"
      class="mt-3"
      :label-class="required ? 'required-field' : ''"
    >
      <template v-slot:label v-if="required && addressLabel">
        {{ addressLabel }} <span class="text-danger">*</span>
      </template>
      
      <div class="address-input-wrapper">
        <div class="custom-select-wrapper" :class="{ 'open': showAddressDropdown, 'disabled': disabled || !localPostcode }">
          <div 
            class="custom-select-trigger" 
            @click="toggleAddressDropdown"
            :class="{ 'disabled': disabled || !localPostcode, 'has-value': localAddress }"
          >
            <span :class="{ 'placeholder': !localAddress }">
              {{ localAddress || addressPlaceholder }}
            </span>
            <svg 
              class="dropdown-icon" 
              :class="{ 'rotated': showAddressDropdown }"
              width="16" 
              height="16" 
              viewBox="0 0 16 16" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M4 6L8 10L12 6" stroke="#666" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          
          <div class="custom-options" v-if="showAddressDropdown && !disabled && localPostcode">
            <div 
              v-if="showEnterManuallyOption" 
              class="custom-option"
              @click="selectAddressManually"
            >
              {{ enterManuallyOptionLabel }}
            </div>
            
            <div 
              v-for="(address, index) in filteredAddresses" 
              :key="index"
              class="custom-option"
              :class="{ 'selected': localAddress === address }"
              @click="selectAddress(address)"
              @mouseenter="activeAddressIndex = index"
            >
              {{ address }}
            </div>
            
            <div v-if="filteredAddresses.length === 0 && !isAddressLoading" class="custom-option disabled">
              No addresses found for this postcode
            </div>
            
            <div v-if="isAddressLoading" class="custom-option loading">
              <b-spinner small type="grow" />
              <span>Loading addresses...</span>
            </div>
          </div>
        </div>
      </div>
      
      <div v-if="showAddressError" class="error-message">
        Please select a valid address from the list.
      </div>
      
      <div v-if="addressValidationError" class="error-message">
        {{ addressValidationError }}
      </div>
    </b-form-group>
  </div>
</template>

<script>
import publicService from '../services/PublicService'

export default {
  name: 'AddressLookup',
  props: {
    postcodeValue: {
      type: String,
      default: ''
    },
    postcodeLabel: {
      type: String,
      default: 'Post code'
    },
    postcodePlaceholder: {
      type: String,
      default: 'Enter post code'
    },
    addressValue: {
      type: String,
      default: ''
    },
    addressLabel: {
      type: String,
      default: 'Address'
    },
    addressPlaceholder: {
      type: String,
      default: 'Enter address'
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    submitted: {
      type: Boolean,
      default: false
    },
    debounceTime: {
      type: Number,
      default: 300
    },
    minChars: {
      type: Number,
      default: 1
    },
    showEnterManuallyOption: {
      type: Boolean,
      default: true
    },
    enterManuallyOptionLabel: {
      type: String,
      default: 'Enter address manually'
    }
  },
  data() {
    return {
      localPostcode: '',
      postcodeSuggestions: [],
      filteredPostcodes: [],
      showPostcodeDropdown: false,
      activePostcodeIndex: -1,
      isPostcodeLoading: false,
      postcodeSearchTimeout: null,
      postcodeValidationError: '',
      isPostcodeFocused: false,
      localAddress: '',
      addressSuggestions: [],
      filteredAddresses: [],
      showAddressDropdown: false,
      activeAddressIndex: -1,
      isAddressLoading: false,
      addressValidationError: '',
      addressSubmitted: false
    }
  },
  computed: {
    isPostcodeValid() {
      if (!this.submitted && !this.showPostcodeError) return null
      if (!this.required) return null
      return this.localPostcode ? true : false
    },
    showPostcodeError() {
      return (this.submitted || this.addressSubmitted) && this.required && !this.localPostcode
    },
    
    isAddressValid() {
      if (!this.submitted && !this.showAddressError) return null
      if (!this.required) return null
      return this.localAddress ? true : false
    },
    showAddressError() {
      return (this.submitted || this.addressSubmitted) && this.required && !this.localAddress && this.localPostcode
    }
  },
  watch: {
    postcodeValue: {
      immediate: true,
      handler(newVal) {
        if (newVal !== this.localPostcode) {
          this.localPostcode = newVal
          if (newVal) {
            this.fetchAddressesForPostcode(newVal)
          }
        }
      }
    },
    addressValue: {
      immediate: true,
      handler(newVal) {
        if (newVal !== this.localAddress) {
          this.localAddress = newVal
        }
      }
    },
    localPostcode(newVal) {
      this.$emit('update:postcodeValue', newVal)
      this.$emit('postcode-change', newVal)
      
      if (this.postcodeValidationError) {
        this.postcodeValidationError = ''
      }
      
      if (this.localAddress) {
        this.localAddress = ''
        this.$emit('update:addressValue', '')
      }
      
      this.addressSuggestions = []
      this.filteredAddresses = []
    },
    localAddress(newVal) {
      this.$emit('update:addressValue', newVal)
      this.$emit('address-change', newVal)
      
      if (this.addressValidationError) {
        this.addressValidationError = ''
      }
      this.addressSubmitted = false
    }
  },
  methods: {
    onPostcodeInput(value) {
      if (this.postcodeSearchTimeout) {
        clearTimeout(this.postcodeSearchTimeout)
      }
      
      const trimmedValue = value ? value.trim() : ''
      
      if (!trimmedValue) {
        this.filteredPostcodes = []
        this.showPostcodeDropdown = false
        this.activePostcodeIndex = -1
        this.isPostcodeLoading = false
        return
      }
      
      if (trimmedValue.length >= this.minChars) {
        this.isPostcodeLoading = true
        
        this.postcodeSearchTimeout = setTimeout(() => {
          this.lookupPostcode(trimmedValue)
        }, this.debounceTime)
      } else {
        this.filteredPostcodes = []
        this.showPostcodeDropdown = false
        this.activePostcodeIndex = -1
      }
    },
    async lookupPostcode(partial) {
      try {
        const results = await publicService.lookupPostcode(partial)
        
        if (results && Array.isArray(results) && results.length > 0) {
          this.postcodeSuggestions = results
          this.filteredPostcodes = results.slice(0, 10)
          this.showPostcodeDropdown = this.filteredPostcodes.length > 0 && this.isPostcodeFocused
          this.activePostcodeIndex = -1
        } else {
          this.postcodeSuggestions = []
          this.filteredPostcodes = []
          this.showPostcodeDropdown = false
        }
      } catch (error) {
        console.error('Error looking up postcode:', error)
        this.postcodeSuggestions = []
        this.filteredPostcodes = []
        this.showPostcodeDropdown = false
        
        if (error.message === 'Failed to fetch') {
          this.postcodeValidationError = 'Network error. Please try again.'
        }
      } finally {
        this.isPostcodeLoading = false
      }
    },
    selectPostcode(postcode) {
      this.localPostcode = postcode
      this.filteredPostcodes = []
      this.showPostcodeDropdown = false
      this.activePostcodeIndex = -1
      this.postcodeValidationError = ''
      this.isPostcodeLoading = false
      
      if (this.postcodeSearchTimeout) {
        clearTimeout(this.postcodeSearchTimeout)
      }
      
      this.$emit('postcode-select', postcode)
      this.fetchAddressesForPostcode(postcode)
    },
    async fetchAddressesForPostcode(postcode) {
      try {
        this.isAddressLoading = true
        this.$emit('addresses-loading', true)
        
        const cleanPostcode = postcode.replace(/ /g, '')
        const addresses = await publicService.getAddressesByPostcode(cleanPostcode)
            
        if (addresses && Array.isArray(addresses) && addresses.length > 0) {
            const cleanedAddresses = addresses.map(addr => {
                const parts = addr.split(',').filter(part => part && part.trim() !== '')
                return parts.join(', ')
            })
            
            this.addressSuggestions = cleanedAddresses
            this.filteredAddresses = cleanedAddresses
            this.$emit('addresses-loaded', cleanedAddresses)
        } else {
            this.addressSuggestions = []
            this.filteredAddresses = []
            this.$emit('addresses-loaded', [])
        }
      } catch (error) {
        console.error('Error fetching addresses:', error)
        this.$emit('error', error)
        this.$emit('addresses-loaded', [])
        this.addressSuggestions = []
        this.filteredAddresses = []
      } finally {
        this.isAddressLoading = false
        this.$emit('addresses-loading', false)
      }
    },
    toggleAddressDropdown() {
      if (!this.disabled && this.localPostcode) {
        this.showAddressDropdown = !this.showAddressDropdown
        
        if (this.showAddressDropdown) {
          this.filteredAddresses = this.addressSuggestions
        }
      }
    },
    selectAddress(address) {
      this.localAddress = address
      this.showAddressDropdown = false
      this.activeAddressIndex = -1
      this.addressSubmitted = false
      this.addressValidationError = ''
      
      this.$emit('address-select', address)
    },
    selectAddressManually() {
      this.showAddressDropdown = false
      this.$emit('enter-manually-selected', true)
    },
    onPostcodeArrowDown() {
      if (this.filteredPostcodes.length > 0) {
        this.activePostcodeIndex = (this.activePostcodeIndex + 1) % this.filteredPostcodes.length
        this.scrollToActivePostcode()
      }
    },
    onPostcodeArrowUp() {
      if (this.filteredPostcodes.length > 0) {
        this.activePostcodeIndex = this.activePostcodeIndex <= 0 
          ? this.filteredPostcodes.length - 1 
          : this.activePostcodeIndex - 1
        this.scrollToActivePostcode()
      }
    },
    onPostcodeEnterKeyPress() {
      if (this.filteredPostcodes.length > 0 && this.activePostcodeIndex >= 0) {
        this.selectPostcode(this.filteredPostcodes[this.activePostcodeIndex])
      } else if (this.filteredPostcodes.length > 0) {
        this.selectPostcode(this.filteredPostcodes[0])
      }
    },
    onPostcodeFocus() {
      this.isPostcodeFocused = true
      if (this.localPostcode && this.localPostcode.length >= this.minChars && this.filteredPostcodes.length > 0) {
        this.showPostcodeDropdown = true
      }
    },
    onPostcodeBlur() {
      this.isPostcodeFocused = false
      setTimeout(() => {
        this.showPostcodeDropdown = false
        this.activePostcodeIndex = -1
      }, 200)
    },
    scrollToActivePostcode() {
      this.$nextTick(() => {
        const activeElement = document.querySelector('.postcode-dropdown .dropdown-item.active')
        if (activeElement) {
          activeElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        }
      })
    },
    validate() {
      this.addressSubmitted = true
      
      let isValid = true
      
      if (this.required && !this.localPostcode) {
        this.postcodeValidationError = 'Please select a valid postcode from the list.'
        isValid = false
      }
      
      if (this.required && this.localPostcode && !this.localAddress) {
        this.addressValidationError = 'Please select a valid address from the list.'
        isValid = false
      }
      
      return isValid
    },
    reset() {
      this.localPostcode = ''
      this.localAddress = ''
      this.filteredPostcodes = []
      this.filteredAddresses = []
      this.addressSuggestions = []
      this.showPostcodeDropdown = false
      this.showAddressDropdown = false
      this.activePostcodeIndex = -1
      this.activeAddressIndex = -1
      this.postcodeValidationError = ''
      this.addressValidationError = ''
      this.isPostcodeLoading = false
      this.isAddressLoading = false
      this.addressSubmitted = false
      
      if (this.postcodeSearchTimeout) {
        clearTimeout(this.postcodeSearchTimeout)
      }
    }
  },
  beforeDestroy() {
    if (this.postcodeSearchTimeout) {
      clearTimeout(this.postcodeSearchTimeout)
    }
  }
}
</script>

<style scoped lang="scss">
.postcode-lookup-container {
  position: relative;
  width: 100%;
  
  .postcode-input-wrapper {
    position: relative;
    width: 100%;
    
    .form-control {
      width: 100%;
      padding-right: 40px;
      
      &:focus {
        outline: none;
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        border-color: #80bdff;
      }
    }
  }
  
  .postcode-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    max-height: 250px;
    overflow-y: auto;
    background-color: white;
    border: 1px solid #ced4da;
    border-top: none;
    border-radius: 0 0 0.25rem 0.25rem;
    z-index: 9999;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    margin-top: -1px;
    
    .dropdown-item {
      padding: 8px 12px;
      cursor: pointer;
      transition: background-color 0.2s;
      font-size: 14px;
      
      &:hover,
      &.active {
        background-color: #f8f9fa;
        color: #0056b3;
        cursor: pointer;
      }
      
      &:not(:last-child) {
        border-bottom: 1px solid #f0f0f0;
      }
    }
  }
  
  .custom-select-wrapper {
    position: relative;
    width: 100%;
    
    .custom-select-trigger {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 12px;
      background-color: #fff;
      border: 1px solid #ced4da;
      border-radius: 0.25rem;
      cursor: pointer;
      transition: all 0.2s;
      min-height: 38px;
      
      &:hover:not(.disabled) {
        border-color: #80bdff;
      }
      
      &.disabled {
        background-color: #e9ecef;
        cursor: not-allowed;
        opacity: 0.7;
      }
      
      .placeholder {
        color: #6c757d;
      }
      
      .dropdown-icon {
        transition: transform 0.2s;
        
        &.rotated {
          transform: rotate(180deg);
        }
      }
    }
    
    .custom-options {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      max-height: 250px;
      overflow-y: auto;
      background-color: white;
      border: 1px solid #ced4da;
      border-top: none;
      border-radius: 0 0 0.25rem 0.25rem;
      z-index: 9999;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      margin-top: -1px;
      z-index: 1;
      
      .custom-option {
        padding: 8px 12px;
        cursor: pointer;
        transition: background-color 0.2s;
        font-size: 14px;
        
        &:hover,
        &.selected {
          background-color: #f8f9fa;
          color: #0056b3;
        }
        
        &:not(:last-child) {
          border-bottom: 1px solid #f0f0f0;
        }
        
        &.disabled {
          color: #6c757d;
          cursor: not-allowed;
          background-color: #f8f9fa;
          
          &:hover {
            background-color: #f8f9fa;
          }
        }
        
        &.loading {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #6c757d;
          cursor: default;
          
          &:hover {
            background-color: white;
          }
        }
      }
    }
  }
  
  .loading-indicator {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: white;
    padding-left: 0.5rem;
    font-size: 0.875rem;
    color: #6c757d;
    pointer-events: none;
    
    ::v-deep .spinner-grow {
      width: 0.875rem;
      height: 0.875rem;
    }
  }
  
  .error-message {
    color: #dc3545;
    font-size: 0.875rem;
    margin-top: 0.25rem;
  }
  
  .required-field::after {
    content: '*';
    color: #dc3545;
    margin-left: 0.25rem;
  }
}

.postcode-dropdown::-webkit-scrollbar,
.custom-options::-webkit-scrollbar {
  width: 6px;
}

.postcode-dropdown::-webkit-scrollbar-track,
.custom-options::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.postcode-dropdown::-webkit-scrollbar-thumb,
.custom-options::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.postcode-dropdown::-webkit-scrollbar-thumb:hover,
.custom-options::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>